import type { FlagType, Targeting } from '@orderlymeds/measurements-schemas';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateFlag } from '../../api/flags';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { Select } from '../../components/Select';
import { TargetingBuilder } from '../../components/TargetingBuilder';
import type { Variant } from '../../components/VariantWeightEditor';
import { nextVariantKey, VariantWeightEditor } from '../../components/VariantWeightEditor';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import { toSlug } from '../../utils/format';
import shared from '../shared.module.css';
import styles from './FlagCreatePage.module.css';

// Extended variant for flag-specific fields
interface FlagVariant extends Variant {
	value: string;
	payload: string;
}

function nextFlagVariantKey() {
	return nextVariantKey();
}

const DEFAULT_VARIANTS: FlagVariant[] = [
	{ key: nextFlagVariantKey(), id: 'on', name: 'On', weight: 5000, value: 'on', payload: '' },
	{ key: nextFlagVariantKey(), id: 'off', name: 'Off', weight: 5000, value: 'off', payload: '' },
];

export function FlagCreatePage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const createFlag = useCreateFlag(id ?? '');
	const breadcrumbs = useProjectBreadcrumbs('Flags', 'New');

	const [flagId, setFlagId] = useState('');
	const [flagIdManuallyEdited, setFlagIdManuallyEdited] = useState(false);
	const [name, setName] = useState('');
	const [type, setType] = useState<FlagType>('boolean');
	const [boolDefault, setBoolDefault] = useState<'true' | 'false'>('false');
	const [multivDefault, setMultivDefault] = useState('');
	const [rollout, setRollout] = useState('100');
	const [variants, setVariants] = useState<FlagVariant[]>(DEFAULT_VARIANTS);
	const [targeting, setTargeting] = useState<Targeting | null>(null);

	const rolloutNum = Math.min(100, Math.max(0, parseInt(rollout, 10) || 0));
	const weightTotal = variants.reduce((sum, v) => sum + v.weight, 0);
	const weightsValid = type === 'boolean' || weightTotal === 10000;

	function handleNameChange(n: string) {
		setName(n);
		if (!flagIdManuallyEdited) {
			setFlagId(toSlug(n));
		}
	}

	function handleFlagIdChange(v: string) {
		setFlagIdManuallyEdited(true);
		setFlagId(v);
	}

	// VariantWeightEditor only manages id, name, weight, key.
	// We keep the extra fields (value, payload) in our own state array.
	function handleVariantsChange(updated: Variant[]) {
		// Merge updated base fields back into our FlagVariant array
		const updatedFlagVariants: FlagVariant[] = updated.map((v) => {
			const existing = variants.find((fv) => fv.key === v.key);
			return {
				...v,
				value: existing?.value ?? v.id,
				payload: existing?.payload ?? '',
			};
		});
		setVariants(updatedFlagVariants);
	}

	function updateVariantExtra(key: string, field: 'value' | 'payload', val: string) {
		setVariants((prev) => prev.map((v) => (v.key === key ? { ...v, [field]: val } : v)));
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!weightsValid) return;

		const defaultValue: boolean | string =
			type === 'boolean' ? boolDefault === 'true' : multivDefault;

		const flagVariants =
			type === 'multivariate'
				? variants.map((v) => {
						const row: {
							id: string;
							value: string;
							weight: number;
							payload?: Record<string, unknown>;
						} = {
							id: v.id.trim(),
							value: v.value.trim(),
							weight: v.weight,
						};
						if (v.payload.trim()) {
							try {
								row.payload = JSON.parse(v.payload.trim()) as Record<string, unknown>;
							} catch {
								// ignore invalid JSON payload
							}
						}
						return row;
					})
				: undefined;

		const result = await createFlag.mutateAsync({
			id: flagId.trim(),
			name: name.trim(),
			type,
			default_value: defaultValue,
			rollout_percentage: rolloutNum,
			variants: flagVariants,
			targeting: targeting ?? undefined,
		});

		navigate(`/projects/${id}/flags/${result.id}`);
	}

	return (
		<div>
			<PageHeader title="New Flag" breadcrumbs={breadcrumbs} />

			<div className={shared.content}>
				<Card>
					<form onSubmit={handleSubmit} className={shared.form}>
						<Input
							label="Name"
							name="flag-name"
							value={name}
							onChange={handleNameChange}
							required
							placeholder="Dark Mode"
						/>
						<Input
							label="Flag ID"
							name="flag-id"
							value={flagId}
							onChange={handleFlagIdChange}
							required
							mono
							placeholder="dark-mode"
						/>
						<Select
							label="Type"
							name="flag-type"
							value={type}
							onChange={(v) => setType(v as FlagType)}
							options={[
								{ value: 'boolean', label: 'Boolean' },
								{ value: 'multivariate', label: 'Multivariate' },
							]}
						/>

						{type === 'boolean' ? (
							<Select
								label="Default Value"
								name="flag-default-bool"
								value={boolDefault}
								onChange={(v) => setBoolDefault(v as 'true' | 'false')}
								options={[
									{ value: 'false', label: 'false' },
									{ value: 'true', label: 'true' },
								]}
							/>
						) : (
							<Input
								label="Default Value"
								name="flag-default-multi"
								value={multivDefault}
								onChange={setMultivDefault}
								mono
								placeholder="off"
							/>
						)}

						<div className={styles.rolloutSection}>
							<label htmlFor="flag-rollout" className={styles.rolloutLabel}>
								Rollout Percentage
							</label>
							<div className={styles.rolloutInputRow}>
								<input
									id="flag-rollout"
									type="number"
									className={styles.rolloutInput}
									value={rollout}
									onChange={(e) => setRollout(e.target.value)}
									min={0}
									max={100}
									required
								/>
								<span className={styles.rolloutPercent}>%</span>
							</div>
							<div className={styles.rolloutBar}>
								<div className={styles.rolloutFill} style={{ width: `${rolloutNum}%` }} />
							</div>
						</div>

						{type === 'multivariate' && (
							<div className={styles.section}>
								<h3 className={shared.sectionTitle}>Variants</h3>
								<VariantWeightEditor
									variants={variants}
									onChange={handleVariantsChange}
									minVariants={2}
								/>

								{/* Extra fields for multivariate flag variants */}
								<div className={styles.variantExtras}>
									<div className={styles.variantExtrasHeader}>
										<span className={styles.variantColHeader}>Variant</span>
										<span className={styles.variantColHeader}>Value</span>
										<span className={styles.variantColHeader}>Payload (JSON)</span>
									</div>
									{variants.map((v) => (
										<div key={v.key} className={styles.variantExtrasRow}>
											<span className={[styles.variantName, shared.mono].join(' ')}>
												{v.id || '—'}
											</span>
											<input
												className={[styles.variantInput, shared.mono].join(' ')}
												type="text"
												value={v.value}
												onChange={(e) => updateVariantExtra(v.key, 'value', e.target.value)}
												placeholder="on"
												required
											/>
											<input
												className={[styles.variantInput, shared.mono].join(' ')}
												type="text"
												value={v.payload}
												onChange={(e) => updateVariantExtra(v.key, 'payload', e.target.value)}
												placeholder="{}"
											/>
										</div>
									))}
								</div>
							</div>
						)}

						<div className={styles.section}>
							<TargetingBuilder value={targeting} onChange={setTargeting} />
						</div>

						{createFlag.error && (
							<p className={shared.formError}>
								{createFlag.error instanceof Error
									? createFlag.error.message
									: 'Failed to create flag'}
							</p>
						)}

						<div className={shared.formActions}>
							<Button
								variant="secondary"
								type="button"
								onClick={() => navigate(`/projects/${id}/flags`)}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								type="submit"
								loading={createFlag.isPending}
								disabled={!name.trim() || !flagId.trim() || !weightsValid}
							>
								Create Flag
							</Button>
						</div>
					</form>
				</Card>
			</div>
		</div>
	);
}
